<template>
  <div class="property-form-row">
    <!-- O nome nasce da coleção do outro lado — no singular ou no plural, conforme o lado aponta para um
         ou para vários —, e digitar o torna manual: o ComputedInput, o mesmo dos alias -->
    <ontos-computed-input :label="$tt('relationship-name')" class="required unique" hide-details="auto"
                          :value="model.name"
                          :manual="manualName" @input:manual="manualName = $event"
                          :source="nameSource" :compute="computeName"
                          :error-messages="errors.name"
                          @input="model.name = $event; check('name')" @blur="check('name')"/>
    <ontos-alias-fields :alias="model.alias" :source="model.name" :fixed="!!currentAlias"
                        v-model:manual="manualAlias" :error-message="errors.alias"
                        @input="model.alias = $event; check('alias')" @blur="check('alias')"/>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import ComputedInput from '@/components/shared/ComputedInput.vue'
import AliasFields from '@/components/design/property/AliasFields.vue'
import {namingError} from '@/utils/fieldValidation'

/** O nome e o identificador de um lado digitados à mão, ou não. */
export type SideManual = { name: boolean, alias: boolean }

/**
 * O nome e o identificador de UM lado de um relacionamento — o dono ou o espelho, na coleção dele.
 * O editor da associação põe dois, lado a lado, e os dois se conferem do mesmo jeito: cada um contra os
 * irmãos da própria coleção (`siblings`), que o editor monta — no relacionamento de uma coleção com ela
 * mesma, o outro lado é um dos irmãos.
 *
 * Mexe direto no `model` do editor, como o `PropertyBehavior`.
 */
@Component({
  name: 'ontos-relationship-side',
  components: {'ontos-computed-input': ComputedInput, 'ontos-alias-fields': AliasFields}
})
class RelationshipSide extends Vue {
  @Prop({type: Object, required: true}) readonly model!: { name: string, alias: string }
  /** As propriedades com que o nome e o identificador não podem repetir. */
  @Prop({type: Array, required: true}) readonly siblings!: Array<{ name: string, alias: string }>
  /** O identificador gravado: não muda, que é a chave nos itens. Vazio no lado que nasce agora. */
  @Prop({type: String, default: ''}) readonly currentAlias!: string
  @Prop({required: true}) readonly nameSource!: unknown
  @Prop({type: Function, required: true}) readonly computeName!: (source: any) => string
  /** Que lado é — o dono ou o espelho —, para o editor saber de quem é o que o `manual` devolve. */
  @Prop({type: String, required: true}) readonly sideKey!: string
  /** Se o nome e o identificador estavam digitados à mão na janela, ao expandir — ver o `manualFlags`. */
  @Prop({type: Object, default: null}) readonly manual!: SideManual | null

  manualName = false
  manualAlias = false
  errors = {name: '', alias: ''}

  /**
   * O nome sugerido já ao abrir: o ComputedInput só calcula quando a fonte MUDA, e a do espelho que
   * nasce agora já chega pronta. Nome que já existe é de alguém, e fica. Expandido, como estava na janela.
   */
  mounted() {
    if (this.manual) {
      this.manualName = this.manual.name
      this.manualAlias = this.manual.alias
      return
    }
    this.manualName = !!this.model.name
    if (!this.manualName) this.model.name = this.computeName(this.nameSource)
  }

  /** O que levar ao expandir: sem isso, mudar o nome na página recalcularia o identificador digitado. */
  manualFlags(): SideManual {
    return {name: this.manualName, alias: this.manualAlias}
  }

  check(prop: 'name' | 'alias'): boolean {
    this.errors[prop] = namingError(this.model[prop], prop, this.siblings)
    return !this.errors[prop]
  }

  /** Os dois, e não até o primeiro que falha: cada campo acusa o seu de uma vez. */
  validate(): boolean {
    return [this.check('name'), this.check('alias')].every(Boolean)
  }
}

export default toNative(RelationshipSide)
</script>
